import type { OMSForm } from '@src/providers/OMSContext.types';
import { ParameterKeysEnum } from '@src/tokens/orderStrategy';
import { calcDateFromDuration, isDuration, parseDate, type Duration } from '@talos/kyoko';
import { camelCase, isNil } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';

/**
 * Calculate the start time of an order, updating if the start time is specified as a Duration.
 *
 * @param order OMS Form for a new or existing order
 * @returns Order start time (existing orders) or expected start time (new orders)
 */
export function useOrderStartTime(order: OMSForm) {
  const { parameters, startTime: formStartTime, submitTime: formSubmitTime } = order;
  const [orderStartTime, setOrderStartTime] = useState<Date>(parseDate(null));

  const recalcStartTime = useCallback(
    function recalcStartTime() {
      if (!isNil(formStartTime)) {
        return setOrderStartTime(parseDate(formStartTime));
      } else if (parameters[camelCase(ParameterKeysEnum.StartTime) as keyof typeof parameters] != null) {
        // TODO: this param lookup feels wrong (parameter keys are largely is PascalCase, not camelCase)
        const paramValue = parameters[camelCase(ParameterKeysEnum.StartTime) as keyof typeof parameters] as
          | Date
          | Duration;
        if (isDuration(paramValue)) {
          setOrderStartTime(calcDateFromDuration(paramValue, parseDate()));
        } else {
          return setOrderStartTime(parseDate(paramValue));
        }
      } else if (!isNil(formSubmitTime)) {
        return setOrderStartTime(parseDate(formSubmitTime));
      } else {
        setOrderStartTime(parseDate());
      }
    },
    [formStartTime, formSubmitTime, parameters]
  );
  useEffect(() => {
    recalcStartTime();
    const timer = setInterval(recalcStartTime, 1000);
    return () => clearInterval(timer);
  }, [recalcStartTime]);
  return orderStartTime;
}
